import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Alert,
  Badge
} from "react-bootstrap";
import axios from "axios";
import useAuthToken from "../../hooks/useAuthToken";
import AppConstants from "../../AppConstants";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { AddPaymentMethodForm } from "./AddPaymentMethodForm";
import IconHeader from "../../images/logo.png";
import { IoWarningOutline } from "react-icons/io5";
import "./stripeStyles.scss";

// Create a separate axios instance without global interceptors for payment methods
const paymentAxios = axios.create();

// Helper function to check if error is due to token expiration
function isTokenExpiredError(error: any): boolean {
  return error?.response?.status === 401 || error?.response?.status === 403;
}

type SavedMethod = {
  id: string;
  brand?: string;
  last4?: string;
  exp_month?: number;
  exp_year?: number;
  isDefault?: boolean;
};

function normalizeSavedMethods(input: any[]): SavedMethod[] {
  if (!Array.isArray(input)) return [];
  return input.map((m: any) => {
    const card = m?.card || m?.cardDetails || m?.card_info || m?.details || {};
    const brand = m?.brand || card?.brand;
    const last4 = m?.last4 || card?.last4;
    const expMonthRaw =
      m?.exp_month ?? m?.expMonth ?? card?.exp_month ?? card?.expMonth;
    const expYearRaw =
      m?.exp_year ?? m?.expYear ?? card?.exp_year ?? card?.expYear;
    const exp_month =
      typeof expMonthRaw === "string" ? parseInt(expMonthRaw, 10) : expMonthRaw;
    const exp_year =
      typeof expYearRaw === "string" ? parseInt(expYearRaw, 10) : expYearRaw;
    return {
      id:
        m?.stripePaymentMethodId ||
        m?.id ||
        m?.paymentMethodId ||
        m?.payment_method_id ||
        "",
      brand,
      last4,
      exp_month,
      exp_year,
      isDefault: m?.isDefault
    } as SavedMethod;
  });
}

export default function CreateSubscriptionPage() {
  const { userToken, ready } = useAuthToken();

  // Subscription details are sourced from backend or URL; not editable here
  const [plan, setPlan] = useState("");
  const [priceId, setPriceId] = useState("");
  const [maxUsers, setMaxUsers] = useState<number | "">("");
  const [promocode, setPromocode] = useState("");
  const [autoRenew, setAutoRenew] = useState<boolean>(true);
  const [prefillLoaded, setPrefillLoaded] = useState(false);

  const [methods, setMethods] = useState<SavedMethod[]>([]);
  const [selectedMethod, setSelectedMethod] = useState("");
  const [showAddMethod, setShowAddMethod] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string>("");
  const [autopayRequested, setAutopayRequested] = useState<boolean>(false);
  const [tokenExpired, setTokenExpired] = useState(false);
  const autoSubmitDoneRef = useRef<boolean>(false);
  const [userInteracted, setUserInteracted] = useState<boolean>(false);
  const [autoCountdown, setAutoCountdown] = useState<number>(5);

  const stripePromise = useMemo(
    () => loadStripe(AppConstants.STRIPE_PUBLISHABLE_KEY || ""),
    []
  );

  const disabled = useMemo(
    () =>
      !ready ||
      !userToken ||
      loading ||
      !priceId ||
      !plan ||
      !selectedMethod ||
      !maxUsers ||
      !customerId ||
      tokenExpired,
    [
      ready,
      userToken,
      loading,
      priceId,
      plan,
      selectedMethod,
      maxUsers,
      customerId,
      tokenExpired
    ]
  );

  // Prefill from query string as fallback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qPlan = params.get("plan");
    const qPriceId = params.get("priceId");
    const qMaxUsers = params.get("maxUsers");
    const qPromocode = params.get("promocode");
    const qAutoRenew = params.get("autoRenew");
    const qPaymentMethodId = params.get("paymentMethodId");
    const qAutoPay = params.get("autopay");

    if (qPlan) setPlan(qPlan);
    if (qPriceId) setPriceId(qPriceId);
    if (qMaxUsers && !isNaN(Number(qMaxUsers))) setMaxUsers(Number(qMaxUsers));
    if (qPromocode !== null) setPromocode(qPromocode);
    if (qAutoRenew !== null)
      setAutoRenew(qAutoRenew === "true" || qAutoRenew === "1");
    if (qPaymentMethodId) setSelectedMethod(qPaymentMethodId);
    if (qAutoPay && (qAutoPay === "1" || qAutoPay.toLowerCase() === "true"))
      setAutopayRequested(true);
  }, []);

  // Load profile (for customerId) and saved methods
  useEffect(() => {
    const load = async () => {
      if (!ready || !userToken) return;
      setLoading(true);
      setError(null);
      try {
        // Profile → get customerId (driver user id)
        try {
          const prof = await paymentAxios.get(AppConstants.DRIVER_PROFILE_API, {
            headers: { Authorization: `Bearer ${userToken}` }
          });
          const pdata = prof.data?.data || prof.data || {};
          if (pdata?.id) setCustomerId(pdata.id);
        } catch (e: any) {
          if (isTokenExpiredError(e)) {
            setTokenExpired(true);
            return;
          }
          // continue without customerId; submit will error if missing
        }

        // Methods
        const res = await paymentAxios.get(
          AppConstants.PAYMENTS_LIST_METHODS_API,
          {
            headers: { Authorization: `Bearer ${userToken}` }
          }
        );
        const payload = res.data;
        let rawList: any[] = [];
        if (Array.isArray(payload)) rawList = payload as any[];
        else if (Array.isArray(payload?.data?.paymentMethods))
          rawList = payload.data.paymentMethods;
        else if (Array.isArray(payload?.data)) rawList = payload.data;
        else if (Array.isArray(payload?.paymentMethods))
          rawList = payload.paymentMethods;
        else if (Array.isArray(payload?.methods)) rawList = payload.methods;
        const list = normalizeSavedMethods(rawList);
        setMethods(list);
        // Prefer selected from URL; otherwise prefer default from server; otherwise first
        if (!selectedMethod) {
          const def = list.find((m) => m.isDefault);
          if (def?.id) setSelectedMethod(def.id);
          else if (list.length > 0) setSelectedMethod(list[0].id);
        }
      } catch (err: any) {
        if (isTokenExpiredError(err)) {
          setTokenExpired(true);
        } else {
          const apiMessage = err?.response?.data?.message || err?.message;
          setError(apiMessage || "Failed to load subscription data.");
        }
      } finally {
        setLoading(false);
        setPrefillLoaded(true);
      }
    };
    load();
  }, [ready, userToken]);

  // Core submit logic used by onSubmit and autopay
  const submitCreateSubscription = useCallback(async () => {
    setError(null);
    setMessage(null);
    if (!userToken) return;
    if (!plan || !priceId || !selectedMethod || !maxUsers || !customerId) {
      setError("Please complete all required fields.");
      return;
    }
    try {
      setLoading(true);
      const payload = {
        customerId,
        plan,
        priceId,
        maxUsers: Number(maxUsers),
        promocode,
        autoRenew: !!autoRenew,
        paymentMethodId: selectedMethod
      };
      console.log(
        "CreateSubscription POST",
        AppConstants.SUBSCRIPTION_CREATE_API,
        payload
      );
      const res = await paymentAxios.post(
        AppConstants.SUBSCRIPTION_CREATE_API,
        payload,
        {
          headers: { Authorization: `Bearer ${userToken}` }
        }
      );
      const body = res.data || {};
      const data = body?.data || body || {};
      if (res.status >= 200 && res.status < 300) {
        const clientSecret: string | undefined =
          data?.clientSecret || data?.latestInvoiceClientSecret;
        if (clientSecret) {
          const stripe = await loadStripe(
            AppConstants.STRIPE_PUBLISHABLE_KEY || ""
          );
          if (!stripe) throw new Error("Stripe failed to initialize");
          const confirmRes = await stripe.confirmCardPayment(clientSecret, {
            // Use an HTTPS return page which will deep-link to the app
            return_url: `${window.location.origin}/stripe/return?flow=sub`
          });
          if (confirmRes.error) {
            setError(
              confirmRes.error.message || "Payment confirmation failed."
            );
          } else {
            // Navigate to return page for consistent UX even when 3DS2 completes in-place
            const url = new URL(
              "/stripe/return",
              window.location.origin
            );
            url.searchParams.set("flow", "sub");
            url.searchParams.set(
              "payment_intent_client_secret",
              clientSecret
            );
            const status = confirmRes.paymentIntent?.status;
            if (status) url.searchParams.set("redirect_status", status);
            window.location.assign(url.toString());
            return; // Prevent further local handling
          }
        } else {
          setMessage(data?.message || "Subscription created successfully.");
          // Redirect to mobile app after successful subscription (no payment confirmation needed)
          setTimeout(() => {
            window.location.href = "pretrip://subscription-success";
          }, 2000);
        }
      } else {
        setError(data?.message || "Failed to create subscription.");
      }
    } catch (err: any) {
      if (isTokenExpiredError(err)) {
        setTokenExpired(true);
        setError(null);
      } else {
        const apiMessage = err?.response?.data?.message || err?.message;
        setError(apiMessage || "Failed to create subscription.");
      }
    } finally {
      setLoading(false);
    }
  }, [
    userToken,
    customerId,
    plan,
    priceId,
    maxUsers,
    promocode,
    autoRenew,
    selectedMethod
  ]);

  // Optional auto-confirm if everything is present and autopay was requested.
  // Show a short countdown and allow cancel/change before paying.
  useEffect(() => {
    if (!autopayRequested) return;
    if (autoSubmitDoneRef.current) return;
    if (!prefillLoaded || disabled) return;
    if (userInteracted) return;

    let remaining = 5;
    setAutoCountdown(remaining);

    const intId = window.setInterval(() => {
      remaining -= 1;
      setAutoCountdown(remaining);
      if (remaining <= 0) {
        window.clearInterval(intId);
      }
    }, 1000);

    const timeoutId = window.setTimeout(() => {
      autoSubmitDoneRef.current = true;
      submitCreateSubscription();
    }, remaining * 1000);

    return () => {
      window.clearInterval(intId);
      window.clearTimeout(timeoutId);
    };
  }, [
    autopayRequested,
    prefillLoaded,
    disabled,
    userInteracted,
    submitCreateSubscription
  ]);

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      submitCreateSubscription();
    },
    [submitCreateSubscription]
  );

  // Refresh methods after adding a new payment method
  const refreshMethods = useCallback(
    async (preferredId?: string) => {
      if (!userToken) return;
      try {
        const res = await paymentAxios.get(
          AppConstants.PAYMENTS_LIST_METHODS_API,
          {
            headers: { Authorization: `Bearer ${userToken}` }
          }
        );
        const payload = res.data;
        let rawList: any[] = [];
        if (Array.isArray(payload)) rawList = payload as any[];
        else if (Array.isArray(payload?.data?.paymentMethods))
          rawList = payload.data.paymentMethods;
        else if (Array.isArray(payload?.data)) rawList = payload.data;
        else if (Array.isArray(payload?.paymentMethods))
          rawList = payload.paymentMethods;
        else if (Array.isArray(payload?.methods)) rawList = payload.methods;
        const list = normalizeSavedMethods(rawList);
        setMethods(list);
        if (preferredId && list.some((m) => m.id === preferredId)) {
          setSelectedMethod(preferredId);
        } else if (!selectedMethod && list.length > 0) {
          setSelectedMethod(list[0].id);
        }
      } catch (err) {
        // best-effort; errors will show during submit
      }
    },
    [userToken, selectedMethod]
  );

  return (
    <section className="dashboard payment-method-page">
      <Container fluid>
        <Row className="justify-content-md-center">
          <Col xl={8} lg={10} sm={12}>
            <figure className="text-center mb-4">
              <img src={IconHeader} alt="logo" width={250} />
            </figure>
            <Card className="shadow-sm" style={{ borderRadius: 16 }}>
              <Card.Body className="px-4 pb-4">
                <h2 className="text-center mb-4">Confirm Subscription</h2>

                {!ready ? (
                  <div className="text-center">
                    <p>Initializing…</p>
                  </div>
                ) : !userToken ? (
                  <Alert variant="danger" className="text-center">
                    <IoWarningOutline />
                    <br /> Missing token. Please open via mobile app.
                  </Alert>
                ) : tokenExpired ? (
                  <div className="text-center py-5">
                    <Alert variant="danger">
                      <h5>Token Expired</h5>
                      <p className="mb-0">
                        Your session has been expired/invalid. Please return to
                        the mobile app and try again.
                      </p>
                    </Alert>
                  </div>
                ) : (
                  <form id="subscription-confirm-form" onSubmit={onSubmit}>
                    {/* Subscription Details Card */}
                    <Card
                      className="mb-4"
                      style={{
                        backgroundColor: "#f8f9fa",
                        border: "1px solid #e9ecef"
                      }}
                    >
                      <Card.Body>
                        <h6 className="card-title mb-3">
                          Subscription Details
                        </h6>
                        <Row>
                          <Col md={6} className="mb-3">
                            <div className="text-muted small">Max Users</div>
                            <div className="fw-semibold">{maxUsers || "-"}</div>
                          </Col>
                          <Col md={6} className="mb-3">
                            <div className="text-muted small">Promocode</div>
                            <div>{promocode || "—"}</div>
                          </Col>
                          <Col md={6}>
                            <div className="text-muted small">Auto Renew</div>
                            <div>
                              <Badge bg={autoRenew ? "success" : "secondary"}>
                                {autoRenew ? "Yes" : "No"}
                              </Badge>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>

                    {/* Auto-confirm countdown alert */}
                    {autopayRequested && !userInteracted && (
                      <Alert variant="warning" className="mb-4">
                        <div className="d-flex justify-content-between align-items-center">
                          <span>
                            Auto-confirming in <strong>{autoCountdown}s</strong>
                            . You can change your card below.
                          </span>
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={() => setUserInteracted(true)}
                          >
                            Cancel auto-confirm
                          </Button>
                        </div>
                      </Alert>
                    )}

                    {/* Payment Method Section */}
                    <div className="mb-4">
                      <h6 className="mb-3">Payment Method</h6>

                      {!Array.isArray(methods) || methods.length === 0 ? (
                        <Card style={{ backgroundColor: "#f8f9fa" }}>
                          <Card.Body className="text-center text-muted py-4">
                            No saved payment methods
                          </Card.Body>
                        </Card>
                      ) : (
                        (() => {
                          const current =
                            methods.find((m) => m.id === selectedMethod) ||
                            methods.find((m) => m.isDefault) ||
                            methods[0];
                          const label = `${
                            current?.brand
                              ? current.brand.toUpperCase()
                              : "CARD"
                          } •••• ${current?.last4 || ""} ${
                            current?.exp_month && current?.exp_year
                              ? `(Exp ${current.exp_month}/${String(
                                  current.exp_year
                                ).slice(-2)})`
                              : ""
                          }`;
                          return (
                            <Card style={{ backgroundColor: "#f8f9fa" }}>
                              <Card.Body>
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <div className="fw-semibold">{label}</div>
                                    <Badge bg="success" className="mt-1">
                                      Primary
                                    </Badge>
                                  </div>
                                </div>
                              </Card.Body>
                            </Card>
                          );
                        })()
                      )}

                      {/* Add Payment Method Section */}
                      <div className="mt-3">
                        {!showAddMethod ? (
                          <Button
                            variant="outline-primary"
                            onClick={() => {
                              setShowAddMethod(true);
                              setUserInteracted(true);
                            }}
                          >
                            + Add a new payment method
                          </Button>
                        ) : (
                          <Card className="mt-3">
                            <Card.Body>
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">Add Payment Method</h6>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => setShowAddMethod(false)}
                                >
                                  Close
                                </Button>
                              </div>
                              <Elements stripe={stripePromise}>
                                <AddPaymentMethodForm
                                  inline
                                  compact={false}
                                  enableSelection
                                  listStyle="list"
                                  onTokenExpired={() => setTokenExpired(true)}
                                  selectedMethodId={selectedMethod}
                                  onSelectSavedMethod={(id) => {
                                    setSelectedMethod(id);
                                    setUserInteracted(true);
                                  }}
                                  onAdded={(pmId?: string) => {
                                    setShowAddMethod(false);
                                    setUserInteracted(true);
                                    refreshMethods(pmId);
                                  }}
                                />
                              </Elements>
                            </Card.Body>
                          </Card>
                        )}
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="d-grid">
                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        disabled={disabled}
                        className="py-3 fw-semibold"
                      >
                        {loading ? "Submitting…" : "Confirm and Pay"}
                      </Button>
                    </div>

                    {/* Messages */}
                    {message && (
                      <Alert variant="success" className="mt-3 mb-0">
                        {message}
                      </Alert>
                    )}
                    {error && (
                      <Alert variant="danger" className="mt-3 mb-0">
                        {error}
                      </Alert>
                    )}
                  </form>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </section>
  );
}
